import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO, { collectionPageJsonLd } from '../components/SEO';
import { NEWS_AUTHOR, formatNewsDate, newsPosts } from '../data/news';

const NewsPage = ({ onRequestDemo, onRequestQuote }) => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="News & Updates — ctrl.aero"
        description="Press releases, partnership announcements, and company news from CTRL Systems, developer of the UL101 Aviation Troubleshooter and acoustic imaging cameras."
        canonical="https://ctrl.aero/news"
        jsonLd={collectionPageJsonLd({
          name: 'News & Updates — CTRL Systems',
          description: 'Press releases, partnership announcements, and company news from CTRL Systems.',
          url: 'https://ctrl.aero/news',
          items: newsPosts.map((a) => ({
            type: 'Article',
            name: a.title,
            url: `https://ctrl.aero/news/${a.slug}`,
            datePublished: a.date,
          })),
        })}
      />
      <Header onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />

      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="absolute inset-0 opacity-20">
          <img src="/images/img_0235.jpeg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">News & Updates</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Latest news, product launches, and industry insights from CTRL Systems
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 max-w-5xl mx-auto" data-testid="news-list">
            {newsPosts.map((article) => (
              <Link
                key={article.slug}
                to={`/news/${article.slug}`}
                data-testid={`news-card-${article.slug}`}
                className="group grid md:grid-cols-[280px_1fr] bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border-2 border-slate-100 hover:border-blue-500 transition-colors duration-300 overflow-hidden"
              >
                <div className={`h-52 md:h-full ${article.imageFit === 'contain' ? 'bg-white p-6' : ''}`}>
                  <img
                    src={article.image}
                    alt={article.imageAlt || article.title}
                    className={`w-full h-full ${article.imageFit === 'contain' ? 'object-contain' : 'object-cover'}`}
                    loading="lazy"
                  />
                </div>
                <div className="p-8">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 text-sm text-slate-500">
                    <span className="inline-block px-3 py-1 bg-blue-500 text-white text-xs font-semibold rounded-full">
                      {article.category}
                    </span>
                    <time dateTime={article.date}>{formatNewsDate(article.date)}</time>
                    <span>·</span>
                    <span>{NEWS_AUTHOR}</span>
                  </div>
                  <h2 className="text-2xl font-bold text-[#1e2d3b] mb-3 leading-snug group-hover:text-blue-700 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-slate-600 leading-relaxed mb-4">{article.excerpt}</p>
                  <span className="inline-flex items-center text-sm font-semibold text-blue-600">
                    Read more <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsPage;
